import React, { forwardRef } from "react";
import classNames from "classnames";
import styles from "./sharedTable.module.scss";
import { Table } from "@mantine/core";

export const StyledTableWrapper = forwardRef<
  HTMLDivElement,
  { isDynamic?: boolean } & React.HTMLAttributes<HTMLDivElement>
>(({ isDynamic: _isDynamic, children, className, style, ...rest }, ref) => (
  <div
    ref={ref}
    className={`${styles.styledTableWrapper} ${className ?? ""}`}
    style={style}
    {...rest}>
    {children}
  </div>
));
StyledTableWrapper.displayName = "StyledTableWrapper";

export const DynamicSectionWrapper = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...rest }, ref) => (
  <div
    ref={ref}
    className={`${styles.dynamicSectionWrapper} ${className ?? ""}`}
    {...rest}>
    {children}
  </div>
));
DynamicSectionWrapper.displayName = "DynamicSectionWrapper";

export const StyledTable: React.FC<
  React.ComponentProps<typeof Table>
> = props => <Table className={styles.styledTable} {...props} />;

export const StyledSpaceCell: React.FC<
  React.TdHTMLAttributes<HTMLTableCellElement>
> = ({ children, className, ...rest }) => (
  <td className={`${styles.styledSpaceCell} ${className ?? ""}`} {...rest}>
    {children}
  </td>
);

export const StyledTh: React.FC<
  React.ThHTMLAttributes<HTMLTableCellElement> & {
    red?: boolean;
    showLeftBorder?: boolean;
  }
> = ({ children, className, red, showLeftBorder, style, ...rest }) => (
  <th
    className={classNames(styles.styledTh, className, {
      [styles.showLeftBorderTh]: showLeftBorder,
    })}
    style={{
      ...style,
      color: "var(--mantine-color-thColor-0)",
      border: `1px solid ${red ? "var(--mantine-color-thColor-0)" : "var(--mantine-color-thBorder-0)"}`,
      backgroundColor: red
        ? "var(--mantine-color-redText-0)"
        : "var(--mantine-color-thBackgroundColor-0)",
    }}
    {...rest}>
    {children}
  </th>
);

export const StyledSplitTh: React.FC<React.HTMLAttributes<HTMLElement>> = ({
  children,
  className,
  ...rest
}) => (
  <span className={`${styles.styledSplitTh} ${className ?? ""}`} {...rest}>
    {children}
  </span>
);

export const StyledTr: React.FC<
  React.HTMLAttributes<HTMLTableRowElement> & {
    hover?: boolean;
    pointer?: boolean;
    background?: string;
  }
> = ({ children, className, hover, pointer, background, style, ...rest }) => (
  <tr
    className={`${styles.styledTr} ${hover ? styles.styledTrHover : ""} ${pointer ? styles.styledTrPointer : ""} ${className ?? ""}`}
    style={{
      ...style,
      ...(background ? { backgroundColor: background } : {}),
    }}
    {...rest}>
    {children}
  </tr>
);

export const StyledTd: React.FC<
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    center?: boolean;
    red?: boolean;
    grayed?: boolean;
    noBorder?: boolean;
    withoutImportant?: boolean;
    showLeftBorder?: boolean;
  }
> = ({
  center,
  children,
  className,
  red,
  grayed,
  noBorder,
  withoutImportant,
  showLeftBorder,
  style,
  ...rest
}) => (
  <td
    className={classNames(
      styles.styledTd,
      center
        ? withoutImportant
          ? styles.styledTdCenter
          : styles.styledTdCenterImportant
        : "",
      className,
      {
        [styles.showLeftBorder]: showLeftBorder && !noBorder,
      },
    )}
    style={{
      ...style,
      border: noBorder
        ? "none"
        : `1px solid ${red ? "var(--mantine-color-redText-0)" : "var(--mantine-color-main-9)"}`,
      backgroundColor: grayed
        ? "var(--mantine-color-inputBg-0)"
        : style?.backgroundColor,
    }}
    {...rest}>
    {children}
  </td>
);

export const PaddingTd: React.FC<
  React.TdHTMLAttributes<HTMLTableCellElement>
> = ({ children, className, ...rest }) => (
  <td className={`${styles.paddingTd} ${className ?? ""}`} {...rest}>
    {children}
  </td>
);

export const StyledTd2: React.FC<
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    showTopBorder?: boolean;
    showBottomBorder?: boolean;
    showLeftBorder?: boolean;
    showRightBorder?: boolean;
    isBlankSpacer?: boolean;
  }
> = ({
  children,
  className,
  style,
  showTopBorder = false,
  showBottomBorder = false,
  showLeftBorder = false,
  showRightBorder = false,
  isBlankSpacer = false,
  ...rest
}) => (
  <td
    className={classNames(styles.styledHiddenCell, className, {
      [styles.showTopBorder]: showTopBorder,
      [styles.showBottomBorder]: showBottomBorder,
      [styles.showLeftBorder]: showLeftBorder,
      [styles.showRightBorder]: showRightBorder,
      [styles.styledHiddenBlankSpacer]: isBlankSpacer,
    })}
    style={style}
    {...rest}>
    {children}
  </td>
);

export const StyledDesc: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...rest
}) => (
  <div className={`${styles.styledDesc} ${className ?? ""}`} {...rest}>
    {children}
  </div>
);
